/**
 * navigateWithDesktopSlide
 *
 * Wraps a Desktop-switch `navigate()` call in the browser's View Transitions
 * API so the outgoing Desktop freezes in place (blurred, held static) while
 * the incoming Desktop slides fully across the viewport on top of it —
 * "slide over a blurred backdrop" instead of a plain cross-fade or hard cut.
 *
 * This is the same `document.startViewTransition()` API already used for the
 * Home → Space hero morph (see `useMessageSending.ts` / `Space.tsx`), applied
 * to the document root instead of named sub-elements. Because
 * `::view-transition-old(root)` / `::view-transition-new(root)` default to
 * new-on-top-of-old, holding the old snapshot static+blurred (see App.scss's
 * `.desktop-switch-transition` rules) and sliding the new one in is enough —
 * no manual screenshotting, no keeping the outgoing DesktopShell mounted.
 *
 * Falls back to a plain `navigate()` on browsers without View Transitions
 * support; DesktopShell's own `.desktop-shell-slide-in` CSS animation covers
 * that fallback case — it checks the `viewTransitionActive` route-state flag
 * this helper stamps on whenever it actually engages the browser API, so the
 * two animations never stack on supporting browsers.
 */

import type { NavigateFunction, NavigateOptions } from 'react-router-dom';

export type DesktopSlideDirection = 'forward' | 'backward';

function supportsViewTransition(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document;
}

// DesktopShell fully unmounts/remounts on every switch (key={id} in App.tsx) —
// canvas background, CRDT connection, dock/sidebar/chat all re-initialize from
// scratch, which can take well over a frame or two, especially for a Desktop
// that isn't already warm. Browsers enforce their own internal deadline on how
// long a View Transition's DOM-update callback may run (Chromium aborts with
// "Transition was aborted because of timeout in DOM update" at ~4s) — if we
// let the callback wait on the full remount settling, a slow one blows that
// deadline, the browser silently SKIPS the entire transition (no blur, no
// slide, nothing), and the real content only appears once the remount
// eventually finishes on its own. Racing the "wait for paint" against this
// short cap guarantees the callback always resolves fast enough that the
// browser never aborts — worst case the "new" snapshot is still the Desktop's
// shell mid-mount, which slides in fine while its content fills in normally
// afterward, exactly like a plain (unanimated) navigation would.
export const PAINT_WAIT_TIMEOUT_MS = 220;

function waitForNextPaint(): Promise<void> {
  return new Promise<void>((resolve) => {
    const raceTimer = setTimeout(resolve, PAINT_WAIT_TIMEOUT_MS);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearTimeout(raceTimer);
        resolve();
      });
    });
  });
}

export function navigateWithDesktopSlide(
  navigate: NavigateFunction,
  to: string,
  options?: NavigateOptions,
  direction: DesktopSlideDirection = 'forward',
): void {
  if (!supportsViewTransition()) {
    navigate(to, options);
    return;
  }

  const doc = document as Document & {
    startViewTransition: (callback: () => void | Promise<void>) => { finished: Promise<void> };
  };

  const directionClass = `desktop-switch-transition--${direction}`;
  doc.documentElement.classList.add('desktop-switch-transition', directionClass);

  // Stamped onto route state so DesktopShell's own `.desktop-shell-slide-in`
  // CSS fallback knows this navigation is already being animated by the
  // View Transition and skips itself — otherwise the two would stack.
  const stateWithMarker = { ...(options?.state as Record<string, unknown> | undefined), viewTransitionActive: true };

  const transition = doc.startViewTransition(async () => {
    navigate(to, { ...options, state: stateWithMarker });
    // Give React a couple of frames to commit + paint the new Desktop before
    // the browser snapshots "new" (belt-and-suspenders against a premature/
    // blank snapshot) — but never wait past PAINT_WAIT_TIMEOUT_MS, so a slow
    // remount can't push us into the browser's own abort deadline.
    await waitForNextPaint();
  });

  // `finished` rejects if the browser skips the transition (e.g. a newer
  // transition superseded this one) — that's an expected, non-error outcome
  // here, so swallow it after cleanup rather than leaving an unhandled
  // rejection.
  transition.finished
    .finally(() => {
      doc.documentElement.classList.remove('desktop-switch-transition', directionClass);
    })
    .catch(() => {});
}
